<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <label
    :class="inputWrapperClasses"
    :for="uid"
    tabindex="-1"
  >
    <slot name="prepend" />
    <input
      :id="uid"
      ref="input"
      v-model="inputValue"
      class="sam-input__input"
      v-bind="$attrs"
      :disabled="disabled"
      :readonly="readonly || asField"
      @change="handleChange"
      @input="handleInput"
    />
    <SamIcon
      v-if="!disabled && !asField && clearable && inputHasValue"
      icon="interface-close"
      :size="SAMORA_SIZES.SMALL"
      class="sam-input__icon clear-icon"
      @click.native="clearInput"
    />
    <SamIcon
      v-if="statusIcon && !asField"
      :icon="statusIcon"
      :size="SAMORA_SIZES.SMALL"
      class="sam-input__icon status-icon"
    />
    <slot name="append" />
  </label>
</template>

<script>
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import SamIcon from '../SamIcon/SamIcon.vue';
import SAMORA_SIZES from '../../../utils/constants/samora-sizes.enum';

export const INPUT_STYLES = {
  DEFAULT: SAMORA_STYLES.DEFAULT,
  DANGER: SAMORA_STYLES.DANGER,
  SUCCESS: SAMORA_STYLES.SUCCESS,
};

const styleIconMap = new Map([
  [INPUT_STYLES.DANGER, 'alert-exclamation-triangle'],
  [INPUT_STYLES.SUCCESS, 'interface-confirm-circle'],
]);

let uniqueIdentifierNumber = 0;

export default {
  name: 'SamInput',
  components: {
    SamIcon,
  },
  inheritAttrs: false,
  props: {
    /**
     * The bound model variable
     * @model
     */
    value: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    /**
     * If the input is disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the input is clearable
     * @values true, false
     */
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If the input is on readonly state
     * @values true, false
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the input should look as a field
     * @values true, false
     */
    asField: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The input's samora style to apply
     * @values default, danger, success
     */
    samStyle: {
      type: String,
      required: false,
      default: SAMORA_STYLES.DEFAULT,
      validator: (val) => Object.values(INPUT_STYLES).includes(val),
    },
  },
  data() {
    uniqueIdentifierNumber += 1;

    return {
      SAMORA_SIZES,
      uid: `samora-input__${uniqueIdentifierNumber}`,
      inputValue: '',
    };
  },
  computed: {
    inputWrapperClasses() {
      return [
        `sam-input is-${this.samStyle}`,
        { 'is-disabled': this.disabled },
        { 'is-readonly': this.readonly },
        { 'as-field': this.asField },
      ];
    },
    statusIcon() {
      return styleIconMap.get(this.samStyle);
    },
    inputHasValue() {
      return !!this.inputValue;
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.inputValue = newVal ?? '';
      },
      immediate: true,
    },
  },
  methods: {
    handleInput() {
      /**
       * Triggers when input changes it's value during value input (@input)
       * @event input
       * @property {number, string} model new value set
       */
      this.$emit('input', this.inputValue);
    },
    handleChange() {
      /**
       * Triggers when the input changes it's value (@change)
       * @event change
       * @property {number, string} model new value set
       */
      this.$emit('change', this.inputValue);
    },
    clearInput($event) {
      this.inputValue = '';
      /**
       * Triggers when input's clear button is pressed
       * @event input
       * @property {number, string} model new value set
       */
      this.$emit('input', '');
      /**
       * Triggers when input's clear button is pressed
       * @event clear
       * @property {number, string} $event clear event
       */
      this.$emit('clear', $event);
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-input {
  @extend .px-4, .py-3, .d-flex, .items-center;
  border: $size--0--fourth solid $neutral--60;
  border-radius: $size--1;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;

  &:hover {
    border-color: $informative--main;
  }

  &:focus-within:not(.as-field) {
    outline: 3px solid $informative--border;
  }

  input.sam-input__input {
    @extend .text--body-1, .m-0;
    width: 100%;
    flex-grow: 1;
    border: none;
    outline: none;
    background-color: transparent;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: $neutral--90;
    box-sizing: border-box;

    &:not(:last-child) {
      @extend .pr-2;
    }

    &:not(:first-child) {
      @extend .pl-2;
    }

    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder,
    &::-webkit-input-placeholder,
    &::placeholder {
      color: $neutral--60;
    }
  }

  &__icon {
    &.clear-icon {
      cursor: pointer;

      &:not(:last-child) {
        @extend .pr-2;
      }
    }
  }

  &.is-danger {
    border-color: $error--main;

    &:focus-within:not(.as-field) {
      outline-color: $error--border;
    }

    &:hover {
      border-color: $error--hover;
    }

    .sam-input__icon.status-icon {
      color: $error--main;
    }
  }

  &.is-success {
    border-color: $success--main;

    &:focus-within:not(.as-field) {
      outline-color: $success--border;
    }

    &:hover {
      border-color: $success--hover;
    }

    .sam-input__icon.status-icon {
      color: $success--main;
    }
  }

  &.as-field {
    @extend .px-0;
    border: none;

    .sam-input__input {
      @extend .text--body-2--medium;
    }
  }

  &.is-disabled {
    pointer-events: none;
    background-color: $neutral--20;
    border-color: $neutral--40;

    .sam-input__input {
      color: $neutral--60;

      &::-webkit-input-placeholder,
      &::-moz-placeholder,
      &:-ms-input-placeholder,
      &::-webkit-input-placeholder,
      &::placeholder {
        color: $neutral--40;
      }
    }

    .sam-input__icon.status-icon {
      color: $neutral--60;
    }
  }
}
</style>
